import { useState, useRef, useCallback } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Button } from '../components/ui/button';
import { Form1099PrintView } from '../components/Form1099PrintView';
import { payeeApi } from '../api';
import { toast } from 'sonner';
import { FileText, Printer, Calendar, ChevronDown } from 'lucide-react';

const currentYear = new Date().getFullYear();
const availableYears = Array.from({ length: 5 }, (_, i) => currentYear + i);

export const TaxCompliancePage = () => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedPayees, setExpandedPayees] = useState({});
  const printRef = useRef(null);
  const singlePrintRef = useRef(null);
  const [singlePrintData, setSinglePrintData] = useState(null);

  const handlePrintAll = useReactToPrint({
    contentRef: printRef,
    documentTitle: `All-1099s-${selectedYear}`,
  });

  const handlePrintSingle = useReactToPrint({
    contentRef: singlePrintRef,
    documentTitle: singlePrintData ? `1099-${singlePrintData.payeeName}-${singlePrintData.llcName}-${selectedYear}` : '1099',
  });

  const triggerSinglePrint = useCallback((payee, llcEntry) => {
    setSinglePrintData({
      payee,
      llcEntry,
      payeeName: payee.name,
      llcName: llcEntry.llc?.company_name || 'LLC',
    });
    setTimeout(() => handlePrintSingle(), 100);
  }, [handlePrintSingle]);

  const handleYearSelect = async (year) => {
    setSelectedYear(year);
    setLoading(true);
    setData(null);
    setExpandedPayees({});
    try {
      const response = await payeeApi.getAll1099Data(year);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load 1099 data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const togglePayee = (payeeId) => {
    setExpandedPayees(prev => ({ ...prev, [payeeId]: !prev[payeeId] }));
  };

  const totalCompensation = data?.payees?.reduce((sum, p) => sum + p.total_amount, 0) || 0;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="tax-compliance-title">Tax Compliance</h1>
          <p className="text-sm text-slate-500 mt-1">Generate and print 1099 forms for all qualifying payees</p>
        </div>
      </div>

      {/* Year Selection */}
      {!selectedYear ? (
        <div className="bg-white rounded-lg border p-8" data-testid="year-selection-panel">
          <div className="text-center mb-6">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-slate-800">Select Tax Year</h2>
            <p className="text-sm text-slate-500 mt-1">Choose a year to generate 1099 forms for all payees marked as requiring 1099</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 max-w-2xl mx-auto">
            {availableYears.map(year => (
              <button
                key={year}
                onClick={() => handleYearSelect(year)}
                className="p-5 border-2 border-slate-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors flex flex-col items-center gap-2"
                data-testid={`tax-year-btn-${year}`}
              >
                <Calendar className="w-7 h-7 text-slate-400" />
                <span className="text-xl font-bold text-slate-900">{year}</span>
                <span className="text-xs text-slate-500">Tax Year</span>
              </button>
            ))}
          </div>
        </div>
      ) : loading ? (
        <div className="bg-white rounded-lg border p-12 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-900 mx-auto"></div>
          <p className="text-slate-500 mt-4">Loading 1099 data for {selectedYear}...</p>
        </div>
      ) : data ? (
        <>
          {/* Summary Bar */}
          <div className="bg-white rounded-lg border p-4" data-testid="tax-summary-bar">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <Button variant="outline" size="sm" onClick={() => { setSelectedYear(null); setData(null); }} data-testid="back-to-years-btn">
                  ← Back to Years
                </Button>
                <div className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">{selectedYear}</span>
                  {' '}&middot;{' '}
                  <span className="font-medium">{data.total_payees}</span> payee{data.total_payees !== 1 ? 's' : ''}
                  {' '}&middot;{' '}
                  Total: <span className="font-semibold text-slate-900">
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalCompensation)}
                  </span>
                </div>
              </div>
              {data.total_payees > 0 && (
                <Button onClick={handlePrintAll} data-testid="print-all-1099s-btn">
                  <Printer className="w-4 h-4 mr-2" />
                  Print All 1099s ({data.total_payees})
                </Button>
              )}
            </div>
          </div>

          {data.total_payees === 0 ? (
            <div className="bg-white rounded-lg border p-12 text-center">
              <FileText className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700">No 1099 Data Found</h3>
              <p className="text-sm text-slate-500 mt-2">
                No payees with payments were found for tax year {selectedYear}.<br />
                Make sure payees are marked as "Requires 1099" in Payee Management.
              </p>
            </div>
          ) : (
            <>
              {/* Payee Accordion List (screen view) */}
              <div className="space-y-3 no-print" data-testid="tax-payee-list">
                {data.payees.map((entry) => {
                  const isOpen = expandedPayees[entry.payee.id];
                  return (
                    <div key={entry.payee.id} className="bg-white rounded-lg border overflow-hidden">
                      <button
                        onClick={() => togglePayee(entry.payee.id)}
                        className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors text-left"
                        data-testid={`payee-accordion-${entry.payee.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-4 h-4 text-slate-400" />
                          <span className="font-medium text-slate-800">{entry.payee.name}</span>
                          <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                            {entry.payee.form_1099_type || '1099-NEC'}
                          </span>
                          {entry.per_llc.length > 1 && (
                            <span className="text-xs text-slate-400">{entry.per_llc.length} LLCs</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-slate-900">
                            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(entry.total_amount)}
                          </span>
                          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </div>
                      </button>
                      {isOpen && (
                        <div className="border-t">
                          {entry.per_llc.map((llcEntry, idx) => (
                            <div key={llcEntry.llc_id}>
                              {/* LLC header strip */}
                              <div className="flex items-center justify-between px-5 py-2 bg-slate-100 border-b text-xs">
                                <div className="flex items-center gap-2">
                                  {entry.per_llc.length > 1 && (
                                    <span className="font-semibold text-slate-600">
                                      LLC {idx + 1} of {entry.per_llc.length}:
                                    </span>
                                  )}
                                  <span className="font-medium text-slate-700">{llcEntry.llc?.company_name || 'Unknown LLC'}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-slate-500">
                                    {llcEntry.check_count} check{llcEntry.check_count !== 1 ? 's' : ''} (${llcEntry.check_total.toFixed(2)})
                                    {llcEntry.wire_count > 0 && `, ${llcEntry.wire_count} wire${llcEntry.wire_count !== 1 ? 's' : ''} ($${llcEntry.wire_total.toFixed(2)})`}
                                    {' · '}
                                    <span className="font-semibold text-slate-700">
                                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(llcEntry.total_amount)}
                                    </span>
                                  </span>
                                  <button
                                    onClick={() => triggerSinglePrint(entry.payee, llcEntry)}
                                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium transition-colors"
                                    title={`Print this 1099 for ${llcEntry.llc?.company_name || 'LLC'}`}
                                    data-testid={`print-single-${entry.payee.id}-${llcEntry.llc_id}`}
                                  >
                                    <Printer className="w-3.5 h-3.5" /> Print
                                  </button>
                                </div>
                              </div>
                              {/* Full 1099 form preview */}
                              <div className="overflow-auto bg-white border-b last:border-b-0" data-testid={`preview-${entry.payee.id}-${llcEntry.llc_id}`}>
                                <div className="mx-auto" style={{ maxWidth: '8.5in' }}>
                                  <Form1099PrintView
                                    payee={entry.payee}
                                    llc={llcEntry.llc}
                                    year={selectedYear}
                                    totalAmount={llcEntry.total_amount}
                                    formType={entry.payee.form_1099_type || '1099-NEC'}
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Print-only: all 1099 forms */}
              <div ref={printRef} className="hidden print:block">
                {data.payees.map((entry) =>
                  entry.per_llc.map((llcEntry) => (
                    <div key={`${entry.payee.id}-${llcEntry.llc_id}`} style={{ pageBreakAfter: 'always' }}>
                      <Form1099PrintView
                        payee={entry.payee}
                        llc={llcEntry.llc}
                        year={selectedYear}
                        totalAmount={llcEntry.total_amount}
                        formType={entry.payee.form_1099_type || '1099-NEC'}
                      />
                    </div>
                  ))
                )}
              </div>

              {/* Print-only: single 1099 form */}
              {singlePrintData && (
                <div ref={singlePrintRef} className="hidden print:block">
                  <Form1099PrintView
                    payee={singlePrintData.payee}
                    llc={singlePrintData.llcEntry.llc}
                    year={selectedYear}
                    totalAmount={singlePrintData.llcEntry.total_amount}
                    formType={singlePrintData.payee.form_1099_type || '1099-NEC'}
                  />
                </div>
              )}
            </>
          )}
        </>
      ) : null}
    </div>
  );
};

export default TaxCompliancePage;
